/**
 * FeedbackModal - In-app feedback form dialog.
 *
 * Renders inside the existing Modal.js wrapper (which provides the focus trap and
 * Escape-key handler). Collects:
 *   - Category (bug / suggestion / compliment / question) via radio group
 *   - Description (required, max 2000 chars) via textarea
 *   - Optional screenshot (captured by FeedbackButton before modal opens; user may
 *     redact or opt out via checkbox)
 *   - Up to 3 extra image attachments (png/jpeg/gif, 5 MB each)
 *
 * On submit the handler reads breadcrumbs from sessionStorage (via services/breadcrumbs)
 * and auto-context from services/autoContext, builds a multipart FormData, and calls
 * api.submitFeedback().
 *
 * @module components/FeedbackModal
 */

import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from './Modal';
import RedactionEditor from './RedactionEditor';
import api from '../services/api';
import { getBreadcrumbs } from '../services/breadcrumbs';
import { getAutoContext } from '../services/autoContext';

const CATEGORIES = ['bug', 'suggestion', 'compliment', 'question'];
const MAX_DESC = 2000;
const MAX_ATTACHMENTS = 3;
const MAX_ATTACH_BYTES = 10 * 1024 * 1024; // 10 MB (matches backend, raised 2026-05-26 for invoice PDFs)

// GH #229 Phase 3 — CRM Ops Panel: detect whether the current URL is on a
// known entity detail page so the feedback can be tagged with the specific
// cogslog row it's about. Matches the `linked_entity_type` enum the backend
// accepts. Invoice route removed 2026-08-26 (GH #233) — invoices are vendor
// bills, not the operator's sales pipeline, so they aren't CRM-synced.
const LINKED_ENTITY_PATTERNS = [
  { type: 'vendor', regex: /\/vendors\/(\d+)(?:\/|$|\?|#)/ },
  { type: 'business', regex: /\/businesses\/(\d+)(?:\/|$|\?|#)/ },
];

/**
 * Inspect a URL path and return the linked-entity descriptor if it matches
 * a known entity detail route, otherwise null. The two returned fields
 * (`type`, `id`) mirror the backend's `linked_entity_type` /
 * `linked_entity_id` column pair verbatim.
 *
 * @param {string} pathname - URL pathname from `useLocation().pathname`.
 * @returns {{type: string, id: number}|null} Match descriptor or null.
 */
function detectLinkedEntity(pathname) {
  if (!pathname) return null;
  for (const { type, regex } of LINKED_ENTITY_PATTERNS) {
    const m = pathname.match(regex);
    if (m) {
      const id = parseInt(m[1], 10);
      if (Number.isFinite(id) && id > 0) {
        return { type, id };
      }
    }
  }
  return null;
}

// Same extension set the OCR upload picker accepts (PR #116) plus PDF.
// Users reporting "extraction failed" can now attach the literal file
// that failed — most commonly a PDF or HEIC.
const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,image/heic,image/heif,image/webp,application/pdf,.heic,.heif,.pdf';
const ATTACHMENT_HELP = 'PNG · JPG · GIF · HEIC · WEBP · PDF · 10 MB each';
const ATTACHMENT_EXT_REGEX = /\.(png|jpe?g|gif|heic|heif|webp|pdf)$/i;

/**
 * FeedbackModal component.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {function} props.onClose - Callback to close the modal.
 * @param {Blob|null} props.screenshotBlob - PNG blob captured before modal opened, or null.
 * @param {boolean} props.captureError - True if screenshot capture failed.
 * @param {Object|null} props.user - Currently authenticated user.
 * @param {Object|null} props.activeOrg - Active organisation.
 * @param {Object|null} props.activeBusiness - Active business.
 * @returns {JSX.Element} Modal dialog with feedback form.
 */
function FeedbackModal({
  isOpen,
  onClose,
  screenshotBlob,
  captureError,
  user,
  activeOrg,
  activeBusiness,
}) {
  const [category, setCategory] = useState('bug');
  const [description, setDescription] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(!captureError);
  const [currentScreenshotBlob, setCurrentScreenshotBlob] = useState(screenshotBlob);
  const [showRedactionEditor, setShowRedactionEditor] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [attachmentError, setAttachmentError] = useState('');
  const errorRef = useRef(null);
  // Post-submit success screen shown for ~2 s before auto-close. Mark UAT
  // 2026-08-28: previously the modal called onClose() silently on success
  // with zero user feedback, so the submitter had no idea whether their
  // report went through.
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // GH #229 Phase 3 — CRM Ops Panel: derive the linked entity purely from
  // the current URL (via react-router's useLocation) rather than prop
  // drilling from every entity detail page. The checkbox only renders when
  // a match exists, and defaults to ON so tagging the current record is
  // the frictionless default when the operator IS on a detail page.
  const location = useLocation();
  const linkedEntity = detectLinkedEntity(location && location.pathname);
  const [linkEntityToFeedback, setLinkEntityToFeedback] = useState(true);

  /**
   * Handle form submission: build FormData, call api.submitFeedback, handle responses.
   *
   * @param {React.FormEvent} e - Form submit event.
   * @returns {Promise<void>}
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg('');
    setAttachmentError('');

    if (!description.trim()) {
      setErrorMsg('Description is required.');
      setTimeout(() => errorRef.current && errorRef.current.focus(), 0);
      return;
    }
    if (!CATEGORIES.includes(category)) {
      setErrorMsg('Please select a valid category.');
      setTimeout(() => errorRef.current && errorRef.current.focus(), 0);
      return;
    }

    setSubmitting(true);

    try {
      const breadcrumbs = getBreadcrumbs();
      const autoCtx = getAutoContext({ user, activeOrg, activeBusiness });

      const fd = new FormData();
      fd.append('description', description.trim());
      fd.append('category', category);
      fd.append('include_screenshot', includeScreenshot ? 'true' : 'false');
      fd.append('url', autoCtx.page.url);
      fd.append('route', autoCtx.page.route);
      fd.append('breadcrumbs', JSON.stringify(breadcrumbs));
      fd.append('browser_info', JSON.stringify(autoCtx.browser));

      if (includeScreenshot && currentScreenshotBlob) {
        fd.append('screenshot', currentScreenshotBlob, 'screenshot.png');
      }

      // GH #229 Phase 3 — attach the CRM linkage pair only when the
      // operator is on a matching entity detail page AND has left the
      // checkbox ticked. Serialized as top-level FormData fields (NOT
      // smuggled into browser_info JSON) so the backend can validate and
      // persist them into the `linked_entity_*` columns directly.
      if (linkedEntity && linkEntityToFeedback) {
        fd.append('linked_entity_type', linkedEntity.type);
        fd.append('linked_entity_id', String(linkedEntity.id));
      }

      attachments.forEach((file) => {
        fd.append('attachments', file, file.name);
      });

      await api.submitFeedback(fd);
      // Show a brief success screen inside the modal, then auto-close.
      // The 2 s window is long enough to read but short enough that the
      // user doesn't feel blocked from getting back to what they were doing.
      setSubmitSuccess(true);
      setTimeout(() => { onClose(); }, 2000);
    } catch (err) {
      const status = err.response ? err.response.status : 0;
      if (status === 429) {
        setErrorMsg('Too many submissions — please try again later.');
      } else if (status === 413) {
        // Ruth UAT 2026-05-26: nginx (host or container) returned 413 because
        // the screenshot + payload exceeded the body-size cap. The
        // FeedbackButton downscale step pre-emptively trims big screenshots,
        // but if the user added several attachments or the page render was
        // exceptionally large, we still surface a clear message instead of
        // the generic "something went wrong" toast.
        setErrorMsg(
          'Submission is too large — try unchecking "Include screenshot" or removing some attachments.'
        );
      } else if (status === 400) {
        const detail = err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Invalid submission. Please check your input.';
        setErrorMsg(detail);
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
      setTimeout(() => errorRef.current && errorRef.current.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Handle extra attachment file selection.
   * Validates count, type (image only), and size (5 MB max per file).
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - File input change event.
   */
  function handleAttachmentChange(e) {
    setAttachmentError('');
    const files = Array.from(e.target.files || []);
    const combined = [...attachments, ...files];

    if (combined.length > MAX_ATTACHMENTS) {
      setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      e.target.value = '';
      return;
    }

    // Accept matches the OCR upload pipeline (PR #116) plus PDF — both
    // by MIME type and by filename extension (HEIC files sometimes report
    // an empty type in older browsers, so the extension fallback matters).
    const invalid = files.find((f) => {
      if (f.size > MAX_ATTACH_BYTES) return true;
      const okByMime = f.type.startsWith('image/') || f.type === 'application/pdf';
      const okByExt = ATTACHMENT_EXT_REGEX.test(f.name || '');
      return !okByMime && !okByExt;
    });
    if (invalid) {
      setAttachmentError(
        `Attachments must be ${ATTACHMENT_HELP.replace(/ · /g, ', ')}.`
      );
      e.target.value = '';
      return;
    }

    setAttachments(combined);
    e.target.value = '';
  }

  /**
   * Remove an attachment by index.
   *
   * @param {number} idx - Index to remove.
   */
  function removeAttachment(idx) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  const descCharsLeft = MAX_DESC - description.length;
  const screenshotUrl = currentScreenshotBlob
    ? URL.createObjectURL(currentScreenshotBlob)
    : null;

  if (showRedactionEditor && currentScreenshotBlob) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Redact Screenshot" size="large">
        <RedactionEditor
          imageBlob={currentScreenshotBlob}
          onComplete={(redacted) => {
            setCurrentScreenshotBlob(redacted);
            setShowRedactionEditor(false);
          }}
          onCancel={() => setShowRedactionEditor(false)}
        />
      </Modal>
    );
  }

  // Post-submit success screen — briefly shown before the modal auto-closes
  // so the submitter sees confirmation that their feedback was received.
  if (submitSuccess) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Send Feedback"
        size="large"
        data-feedback-capture-exclude="true"
      >
        <div className="feedback-modal__success" role="status" aria-live="polite">
          <img
            src="/logos/loader/cogslog-loader-light-md.svg"
            width="48"
            height="108"
            alt=""
            aria-hidden="true"
            className="feedback-modal__success-check"
          />
          <h3 className="feedback-modal__success-title">Thanks for the feedback!</h3>
          <p className="feedback-modal__success-body">
            Someone from the team will review this shortly. If you left contact
            details we may follow up directly.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Feedback"
      size="large"
      data-feedback-capture-exclude="true"
    >
      <form
        className="feedback-modal__form"
        onSubmit={handleSubmit}
        aria-label="Feedback form"
        data-feedback-capture-exclude="true"
      >
        {/* Error heading */}
        {errorMsg && (
          <p
            ref={errorRef}
            className="feedback-modal__error-heading"
            role="status"
            aria-live="polite"
            tabIndex={-1}
          >
            {errorMsg}
          </p>
        )}

        {/* GH #229 Phase 3 — Linked-entity checkbox. Only renders when the
            current URL is on a known entity detail route (vendor / business /
            invoice). Ticked by default so tagging the current record is the
            frictionless path when the user is clearly reporting about it. */}
        {linkedEntity && (
          <div className="field feedback-modal__linked-entity">
            <label className="feedback-modal__inline-checkbox">
              <input
                type="checkbox"
                data-testid="feedback-linked-entity-checkbox"
                checked={linkEntityToFeedback}
                onChange={(e) => setLinkEntityToFeedback(e.target.checked)}
                aria-label={`Link this feedback to ${linkedEntity.type} ${linkedEntity.id}`}
              />
              <span>
                This feedback is about the current {linkedEntity.type}{' '}
                <span
                  className="feedback-modal__linked-entity-id"
                  data-testid="feedback-linked-entity-id"
                >
                  #{linkedEntity.id}
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Category — card-chip radios. Each chip is a labeled radio that
            looks like a tappable card; selected state has primary border +
            tinted background. Far more deliberate than the OS-default
            radio circles. */}
        <div
          className="field"
          role="radiogroup"
          aria-labelledby="feedback-category-label"
        >
          <span id="feedback-category-label" className="label">
            Category <span className="req">*</span>
          </span>
          <div className="feedback-modal__category-grid">
            {CATEGORIES.map((cat) => {
              const checked = category === cat;
              return (
                <label
                  key={cat}
                  className={`feedback-modal__category-chip${checked ? ' feedback-modal__category-chip--checked' : ''}`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat}
                    checked={checked}
                    onChange={() => setCategory(cat)}
                    className="feedback-modal__category-chip-input"
                  />
                  <span className="feedback-modal__category-chip-label">
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <div className="field">
          <label className="label" htmlFor="feedback-description">
            Description <span className="req">*</span>
          </label>
          <textarea
            id="feedback-description"
            className="input"
            rows={4}
            maxLength={MAX_DESC}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue or suggestion…"
            aria-required="true"
            aria-invalid={!!errorMsg && !description.trim()}
            aria-describedby={errorMsg && !description.trim() ? 'feedback-desc-error' : undefined}
          />
          <div className="feedback-modal__textarea-footer">
            <span
              className={`feedback-modal__char-counter${descCharsLeft < 200 ? ' feedback-modal__char-counter--warning' : ''}`}
              aria-live="polite"
            >
              {description.length} / {MAX_DESC}
            </span>
          </div>
        </div>

        {/* Screenshot + Extra attachments — side-by-side on wide viewports
            so the form fits without scrolling. Stacks back to single column
            on narrow viewports via the grid's auto-fit. */}
        <div className="feedback-modal__media-grid">
          {(screenshotBlob || captureError) && (
            <div className="field">
              <span className="label">Screenshot</span>
              {captureError ? (
                <div className="feedback-modal__screenshot-card feedback-modal__screenshot-card--empty">
                  <span className="hint">
                    Screenshot unavailable — feedback will submit without one.
                  </span>
                </div>
              ) : (
                <div className="feedback-modal__screenshot-card">
                  {screenshotUrl && (
                    <img
                      src={screenshotUrl}
                      alt="Page screenshot preview"
                      className="feedback-modal__screenshot-thumb"
                    />
                  )}
                  <div className="feedback-modal__screenshot-footer">
                    <label className="feedback-modal__inline-checkbox">
                      <input
                        type="checkbox"
                        checked={includeScreenshot}
                        onChange={(e) => setIncludeScreenshot(e.target.checked)}
                      />
                      Include
                    </label>
                    {includeScreenshot && currentScreenshotBlob && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowRedactionEditor(true)}
                      >
                        Redact
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Extra attachments — styled drop-zone + chip list */}
          <div className="field">
            <label className="label" htmlFor="feedback-attachments">
              Extra attachments{' '}
              <span className="feedback-modal__attachments-label">
                ({attachments.length} of {MAX_ATTACHMENTS})
              </span>
            </label>
            <label
              className={`feedback-modal__dropzone${attachments.length >= MAX_ATTACHMENTS ? ' feedback-modal__dropzone--disabled' : ''}`}
              htmlFor="feedback-attachments"
            >
              <svg
                className="feedback-modal__dropzone-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
              <span className="feedback-modal__dropzone-text">
                <strong>Click to attach</strong>
                <span className="feedback-modal__dropzone-meta">
                  {ATTACHMENT_HELP} · {MAX_ATTACHMENTS - attachments.length} left
                </span>
              </span>
              <input
                id="feedback-attachments"
                type="file"
                accept={ATTACHMENT_ACCEPT}
                multiple
                disabled={attachments.length >= MAX_ATTACHMENTS}
                onChange={handleAttachmentChange}
                className="feedback-modal__dropzone-input"
              />
            </label>
            {attachmentError && (
              <span className="err-msg" role="status" aria-live="polite">
                {attachmentError}
              </span>
            )}
            {attachments.length > 0 && (
              <ul className="feedback-modal__attachment-list">
                {attachments.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="feedback-modal__attachment-chip">
                    <svg
                      className="feedback-modal__attachment-chip-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="feedback-modal__attachment-chip-name">{f.name}</span>
                    <button
                      type="button"
                      className="feedback-modal__attachment-chip-remove"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => removeAttachment(i)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Submit row — top-bordered footer separates form from actions */}
        <div className="feedback-modal__submit-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? 'Sending…' : 'Send feedback'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default FeedbackModal;
